/**
 * Main dashboard layout component.
 *
 * Renders the navigation header, environment switcher, and tab content.
 *
 * Implementation plan reference: D1.23
 */

import { useState } from 'react';
import { useAuth } from '@/auth/AuthGuard';
import { useRefresh, type RefreshMode } from '@/context/RefreshContext';
import { PipelineTab } from './PipelineTab';
import { ServicesTab } from './ServicesTab';
import { DatabaseTab } from './DatabaseTab';
import { AlertsTab } from './AlertsTab';
import { CostsTab } from './CostsTab';
import { CoverageTab } from './CoverageTab';
import { AnalyticsTab } from './AnalyticsTab';
import { DriftTab } from './DriftTab';

type Environment = 'dev' | 'staging' | 'prod';
type TabId = 'pipeline' | 'services' | 'database' | 'alerts' | 'costs' | 'coverage' | 'analytics' | 'drift';

interface Tab {
  id: TabId;
  label: string;
}

const TABS: Tab[] = [
  { id: 'pipeline', label: 'Pipeline' },
  { id: 'services', label: 'Services' },
  { id: 'database', label: 'Database' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'costs', label: 'Costs' },
  { id: 'coverage', label: 'Coverage' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'drift', label: 'Drift' },
];

export function Dashboard(): JSX.Element {
  const { user, logout } = useAuth();
  const { mode, setMode, requestManualRefresh } = useRefresh();
  const [environment, setEnvironment] = useState<Environment>('dev');
  const [activeTab, setActiveTab] = useState<TabId>('pipeline');

  function renderTabContent(): JSX.Element {
    switch (activeTab) {
      case 'pipeline':
        return <PipelineTab />;
      case 'services':
        return <ServicesTab environment={environment} />;
      case 'database':
        return <DatabaseTab environment={environment} />;
      case 'alerts':
        return <AlertsTab />;
      case 'costs':
        return <CostsTab />;
      case 'coverage':
        return <CoverageTab />;
      case 'analytics':
        return <AnalyticsTab environment={environment} />;
      // DriftTab has its own env-pair selector; does NOT receive the global
      // `environment` state (drift is a two-env comparison, not a single-env
      // view).
      case 'drift':
        return <DriftTab />;
    }
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>Blooph Observability</h1>

        <div className="header-controls">
          <div className="header-refresh-group" role="group" aria-label="Data refresh">
            <label className="header-refresh-label" htmlFor="dashboard-refresh-mode">
              Refresh
            </label>
            <select
              id="dashboard-refresh-mode"
              value={mode}
              onChange={(e): void => setMode(e.target.value as RefreshMode)}
              className="refresh-mode-select"
              aria-label="Refresh mode"
            >
              <option value="manual">Manual</option>
              <option value="auto">Auto</option>
            </select>
            <button
              type="button"
              className="header-refresh-btn"
              onClick={requestManualRefresh}
              aria-label="Refresh current tab data now"
            >
              Refresh now
            </button>
          </div>

          <select
            id="dashboard-environment"
            value={environment}
            onChange={(e): void => setEnvironment(e.target.value as Environment)}
            className="env-selector"
            aria-label="Environment"
          >
            <option value="dev">Development</option>
            <option value="staging">Staging</option>
            <option value="prod">Production</option>
          </select>

          <div className="user-info">
            <span>{user?.name ?? 'Unknown'}</span>
            <span className="user-email">{user?.email ?? ''}</span>
            <button onClick={logout} className="logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>

      <nav className="tab-bar">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={(): void => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <main className="tab-content">
        {renderTabContent()}
      </main>
    </div>
  );
}
